import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { Platform } from '@ionic/angular';
import { SegmentCMOSService } from './segment-cmos.service';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { UserService } from './user.service';
import { UserPushToken } from '@bluecrew/crew-member-backend-angular-client';
import DeviceTypeEnum = UserPushToken.DeviceTypeEnum;

const LAST_SET_REMINDER_KEY = 'NotificationService.LastReminderEpochMillis';
const REMINDER_PERIOD_MILLIS = 1000 * 60 * 60 * 24 * 6;  // Currently 6 days
const isPushNotificationsAvailable = Capacitor.isPluginAvailable('PushNotifications'); // avoid throwing error when plugin is not available in web environment

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  constructor(
    private readonly storage: Storage,
    private readonly platform: Platform,
    private readonly segmentCMOS: SegmentCMOSService,
    private readonly userService: UserService
  ) { 
    if (isPushNotificationsAvailable) {
      PushNotifications.addListener('registration', async (token: Token) => {
        const isIos = this.platform.is('ios');
        await this.userService.storePushToken({
          deviceToken: token.value,
          deviceType: isIos ? DeviceTypeEnum.Ios : DeviceTypeEnum.Android,
        });
      });
    }
  }

  async checkForPeriodicReminder(): Promise<boolean> {
    await this.platform.ready();
    if (!this.platform.is('hybrid')) {
      return false;
    }
    const lastReminderEpoch = await this.getLastReminderEpochMillis();
    if (Date.now() - lastReminderEpoch > REMINDER_PERIOD_MILLIS) {
      try {
        await this.registerPushNotifications();
        return true;
      } catch (e) {
        this.segmentCMOS.addEvent('periodicNotifications_failed');
        console.error('Error requesting notification permissions', e);
      } finally {
        await this.touchLastReminderEpochMillis();
      }
    }
    return false;
  }

  async registerPushNotifications() {
    if (isPushNotificationsAvailable) {
      const result = await PushNotifications.requestPermissions();
      if (result.receive === 'granted') {
        await PushNotifications.register();
      }
    }
  }

  private async getLastReminderEpochMillis(): Promise<number> {
    return (await this.storage.get(LAST_SET_REMINDER_KEY))?.epochMillis || 0;
  }

  private async touchLastReminderEpochMillis(): Promise<void> {
    await this.storage.set(LAST_SET_REMINDER_KEY, { epochMillis: Date.now() });
  }
}
